import {Moment} from 'moment';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {DateRangeType} from '../../../date-range-input/date-range-utils';
import {wait} from '../../../../utils/functions/wait';
import {useSnackBar} from '../../../../contexts/snack-bar/context';
import {translations} from '../../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {EntityCareServiceRepository} from '../../../../repositories/entity-care-services';
import {PostCareService} from '../../../../backend/requests/post-care-service';
import {PutCareService} from '../../../../backend/requests/put-care-service';
import {
  FetchEntityUsersResult,
  useFetchEntityUsers,
} from '../../../../hooks/use-fetch-entity-users';
import {UserType} from '../../../../backend/enums/user-type';
import {AffiliatedUser} from '../../../../backend/responses/affiliated-user';
import {UpdateScope} from '../../../../backend/interfaces/care-service';
import {
  createCareServiceItem,
  transformUserCareServices2CareServiceItems,
} from './care-service-data-transformation';
import {CareServiceItem, CareServicesViewMode} from './care-service-item';

export type CareServicesData = {
  userId: string;
  mode: CareServicesViewMode;
  fetching: boolean;
  updating: boolean;
  items: CareServiceItem[];
  seniors: FetchEntityUsersResult;
  caregivers: FetchEntityUsersResult;
  refresh: () => void;
  post: (body: PostCareService) => Promise<void>;
  put: (fromItem: CareServiceItem, body: PutCareService) => Promise<void>;
  remove: (item: CareServiceItem, deletionScope: UpdateScope) => Promise<void>;
  filterDataBySeniorId: (userId?: AffiliatedUser) => void;
  filterDataByCaregiverId: (userId?: AffiliatedUser) => void;
  reset: () => void;
  redraw: () => void;
};

export function useCareServicesData(opts: {
  /** it can be null, senior id or caregiver id, depending on the view. */
  userId: string;
  entityId: string;
  startDate: Moment;
  endDate: Moment;
  range: DateRangeType;
  /** The way we fetch and display data. */
  mode: CareServicesViewMode;
}): CareServicesData {
  const snackBar = useSnackBar();
  const {t} = useTranslation();
  const [allItems, setAllItems] = useState<CareServiceItem[]>([]);
  const [items, setItems] = useState<CareServiceItem[]>([]);
  const [filterSenior, setFilterSenior] = useState<AffiliatedUser | undefined>();
  const [filterCaregiver, setFilterCaregiver] = useState<AffiliatedUser | undefined>();
  const [fetching, setFetching] = useState(false);
  const [updating, setUpdating] = useState(false);
  const abort = useRef<AbortController | undefined>();
  const timeout = useRef<NodeJS.Timeout | undefined>();
  const seniors = useFetchEntityUsers({
    activeUsers: true,
    entityId: opts.entityId,
    userType: UserType.Senior,
  });
  const caregivers = useFetchEntityUsers({
    activeUsers: true,
    entityId: opts.entityId,
    userType: UserType.Caregiver,
  });

  const fetchRange = useMemo(() => {
    switch (opts.range) {
      case '1d':
        return [1, 'day'];
      case '1w':
      case '7d':
      case '15d':
        return [1, 'week'];
      case '1m':
      case '30d':
        return [1, 'month'];
      default:
        return [1, 'month'];
    }
  }, [opts.range]);

  const refresh = useCallback(() => {
    abort.current?.abort();
    abort.current = new AbortController();
    const QUEUE_LIMIT = 6;
    let inQueue = 0;

    const fetchCareServices = async (start: Moment, end: Moment): Promise<void> => {
      let startSliced = start.clone();
      let endSliced = startSliced.clone().add(fetchRange[0] as any, fetchRange[1]);
      if (endSliced.isAfter(end)) endSliced = end;

      // Increment queue
      inQueue++;
      setFetching(true);

      // Fetch care services
      EntityCareServiceRepository.instance
        .getItems(opts.entityId, opts.userId, startSliced, endSliced, opts.mode, {
          signal: abort.current?.signal,
        })
        .then(async (response) => {
          setAllItems((x) => x.concat(transformUserCareServices2CareServiceItems(response)));
        })
        .catch((error: Error) => {
          if (error.name === 'CanceledError') return;

          snackBar.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.planner.errors.content),
          });
          console.error(error);
        })
        .finally(() => {
          // Decrement queue
          inQueue--;

          // Validate completion
          if (inQueue === 0) {
            // all fetching done
            setFetching(false);
          }
        });

      // Validate completion
      if (endSliced.isSameOrAfter(end)) {
        // all fetching done
        return;
      }

      // Validate queue capacity
      while (inQueue >= QUEUE_LIMIT) {
        // wait a bit & check again
        await wait(100);
      }

      // Queue not in max capacity, fetch next slice
      return fetchCareServices(endSliced, end);
    };

    try {
      // Reset state
      reset();
      // First fetch
      fetchCareServices(opts.startDate, opts.endDate);
    } catch (error) {
      console.log(error);
    }

    // Useffect Cleanup
    return () => {
      abort.current?.abort();
    };
    // ignored: snackBar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRange, opts.entityId, opts.userId, opts.startDate, opts.endDate, opts.mode, t]);

  const post = useCallback(
    async (item: PostCareService) => {
      // Send to backend
      const newCareService = await EntityCareServiceRepository.instance.create(
        opts.entityId,
        item.userId,
        item
      );

      // Validate new data
      if (
        // not for this senior
        (opts.mode === 'senior' && newCareService.userId !== opts.userId) ||
        // not for this caregiver
        (opts.mode === 'caregiver' &&
          !newCareService.associatedCaregiversUserIds?.includes(opts.userId))
      ) {
        return; // ignore it
      }

      // If it has repetition, refresh all data
      if (item.repetition) {
        refresh();
      }
      // Update local state
      else {
        setAllItems((prev) => prev.concat(createCareServiceItem(newCareService)));
      }
    },
    [opts.entityId, opts.mode, opts.userId, refresh]
  );

  const put = useCallback(
    async (fromItem: CareServiceItem, body: PutCareService) => {
      try {
        setUpdating(true);
        // Send to backend
        const response = await EntityCareServiceRepository.instance.update(
          opts.entityId,
          fromItem.id,
          body
        );
        const updatedCareService = response.data;

        // If no updated data returned, or 205 Reset Content
        if (!updatedCareService || response.status === 205) {
          // refresh all data
          refresh();
          return;
        }

        // --- Validate updated data ---
        // If update scope is more than only this instance, refresh all data
        if (body.updateScope !== 'only-this-instance') {
          refresh();
        }
        // If schedule was added, refresh to get correct instances
        else if (!fromItem.repetition && updatedCareService.repetition) {
          refresh();
        }
        // Update local state
        else {
          // If not for this user, remove from list
          if (
            // not for this senior
            (opts.mode === 'senior' && updatedCareService.userId !== opts.userId) ||
            // not for this caregiver
            (opts.mode === 'caregiver' &&
              !updatedCareService.associatedCaregiversUserIds?.includes(opts.userId))
          ) {
            // Remove from list (it was unassigned from this user)
            setAllItems((prev) => prev.filter((i) => i.id !== fromItem.id));
          }
          // Else, update item in list
          else {
            setAllItems((prev) => {
              const updatedCareServiceItem = createCareServiceItem(updatedCareService);
              const idx = prev.findIndex((i) => i.id === updatedCareServiceItem.id);
              if (idx === -1) return prev;
              const next = prev.slice(); // clone
              next[idx] = updatedCareServiceItem;
              return next;
            });
          }
        }
      } finally {
        setUpdating(false);
      }
    },
    [opts.entityId, opts.mode, opts.userId, refresh]
  );

  const remove = useCallback(
    async (item: CareServiceItem, deletionScope: UpdateScope) => {
      try {
        setUpdating(true);
        // Send to backend
        await EntityCareServiceRepository.instance.delete(opts.entityId, item.id, deletionScope);

        // If it has repetition, refresh all data
        if (deletionScope === 'all-instances' || deletionScope === 'this-and-future-instances') {
          refresh();
        }
        // Update local state
        else {
          setAllItems((prev) => prev.filter((i) => i.id !== item.id));
        }
      } finally {
        setUpdating(false);
      }
    },
    [opts.entityId, refresh]
  );

  const reset = () => {
    setFetching(true);
    setUpdating(false);
    setAllItems([]);
  };

  const redraw = useCallback(() => {
    setItems((x) => x.slice());
  }, []);

  const filterDataBySeniorId = useCallback((user: AffiliatedUser | undefined) => {
    setFilterSenior(user);
  }, []);

  const filterDataByCaregiverId = useCallback((user: AffiliatedUser | undefined) => {
    setFilterCaregiver(user);
  }, []);

  // --- FETCH DATA ---
  useEffect(() => {
    clearTimeout(timeout.current);
    abort.current?.abort();
    reset();
    timeout.current = setTimeout(() => refresh(), 500);

    return () => {
      clearTimeout(timeout.current);
      abort.current?.abort();
    };
  }, [refresh, opts.userId]);

  // --- FILTER ITEMS ---
  useEffect(() => {
    let result: CareServiceItem[] = allItems;

    // --- FILTER BY USER ---
    if (filterSenior !== undefined) {
      const newFilteredItems = result.filter((item) => {
        return item.targetUserId === filterSenior.user.id!;
      });
      result = newFilteredItems;
    }

    // --- FILTER BY CAREGIVER ---
    if (filterCaregiver !== undefined) {
      const newFilteredItems = result.filter((item) => {
        return item.responsibleUserIds?.includes(filterCaregiver.user.id!);
      });
      result = newFilteredItems;
    }

    // --- SET FILTER ITEMS ---
    setItems(result);
  }, [allItems, filterSenior, filterCaregiver]);

  return {
    mode: opts.mode,
    userId: opts.userId,
    items,
    fetching,
    updating,
    seniors,
    caregivers,
    reset,
    refresh,
    post,
    put,
    remove,
    redraw,
    filterDataBySeniorId,
    filterDataByCaregiverId,
  };
}
